import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaPrint,
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaBox,
  FaEye,
} from "react-icons/fa";

import {
  Boxes,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const IndividualStockSummary = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(
    ThemeContext
  );

  const currentSkin =
    getCurrentSkin(
      skin
    );

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        userId,
      ]
    );

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId ||
      "8"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    status,
    setStatus,
  ] = useState("");

  /* =========================================================
     DROPDOWN LISTS
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    categoryList,
    setCategoryList,
  ] = useState([]);

  const [
    subCategoryList,
    setSubCategoryList,
  ] = useState([]);

  const [
    innerSubCategoryList,
    setInnerSubCategoryList,
  ] = useState([]);

  const [
    deepInnerSubCategoryList,
    setDeepInnerSubCategoryList,
  ] = useState([]);

  /* =========================================================
     SELECTED IDs
  ========================================================= */

  const [
    selectedCategory,
    setSelectedCategory,
  ] = useState("");

  const [
    selectedSubCategory,
    setSelectedSubCategory,
  ] = useState("");

  const [
    selectedInnerSubCategory,
    setSelectedInnerSubCategory,
  ] = useState("");

  const [
    selectedDeepInnerSubCategory,
    setSelectedDeepInnerSubCategory,
  ] = useState("");

  /* =========================================================
     DATA
  ========================================================= */

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     TEMPLATE DESIGN CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     INITIAL BRANCH + CATEGORY LOAD
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/fillddl_itemRpt",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              8,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setBranchList(
          res.data
            .branchList ||
            []
        );

        setCategoryList(
          res.data
            .categoryList ||
            []
        );
      })
      .catch((err) => {
        console.error(
          "Branch fetch failed",
          err
        );

        toast.error(
          "Failed to load branches"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
     BRANCH CHANGE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    if (!branchId) {
      return;
    }

    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/fillddl_itemRpt",
        {
          params: {
            branchid:
              branchId,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        setCategoryList(
          res.data
            .categoryList ||
            []
        );

        setBranchList(
          res.data
            .branchList ||
            []
        );
      })
      .catch((err) => {
        console.error(
          "Category fetch failed",
          err
        );

        toast.error(
          "Failed to load categories"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    branchId,
    commonHeaders,
  ]);

  /* =========================================================
     SUB CATEGORY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchSubCategories =
    (categoryId) => {
      if (!categoryId) {
        setSubCategoryList([]);

        setInnerSubCategoryList(
          []
        );

        setDeepInnerSubCategoryList(
          []
        );

        setSelectedSubCategory(
          ""
        );

        setSelectedInnerSubCategory(
          ""
        );

        setSelectedDeepInnerSubCategory(
          ""
        );

        return;
      }

      axios
        .get(
          `https://nissi-india.com/api/subcategory_rpt/${categoryId}`,
          {
            headers:
              commonHeaders,
          }
        )
        .then((res) => {
          setSubCategoryList(
            res.data
              .subCategoryList ||
              []
          );

          setInnerSubCategoryList(
            []
          );

          setDeepInnerSubCategoryList(
            []
          );

          setSelectedSubCategory(
            ""
          );

          setSelectedInnerSubCategory(
            ""
          );

          setSelectedDeepInnerSubCategory(
            ""
          );
        })
        .catch((err) => {
          console.error(
            "Sub Category fetch failed",
            err
          );
        });
    };

  /* =========================================================
     INNER SUB CATEGORY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchInnerSubCategories =
    (subCategoryId) => {
      if (!subCategoryId) {
        setInnerSubCategoryList(
          []
        );

        setDeepInnerSubCategoryList(
          []
        );

        setSelectedInnerSubCategory(
          ""
        );

        setSelectedDeepInnerSubCategory(
          ""
        );

        return;
      }

      axios
        .get(
          `https://nissi-india.com/api/innersubcategory_rpt/${subCategoryId}`,
          {
            headers:
              commonHeaders,
          }
        )
        .then((res) => {
          setInnerSubCategoryList(
            res.data
              .innerSubCategoryList ||
              []
          );

          setDeepInnerSubCategoryList(
            []
          );

          setSelectedInnerSubCategory(
            ""
          );

          setSelectedDeepInnerSubCategory(
            ""
          );
        })
        .catch((err) => {
          console.error(
            "Inner Sub Category fetch failed",
            err
          );
        });
    };

  /* =========================================================
     DEEP INNER SUB CATEGORY
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const fetchDeepInnerSubCategories =
    (
      innerSubCategoryId
    ) => {
      if (
        !innerSubCategoryId
      ) {
        setDeepInnerSubCategoryList(
          []
        );

        setSelectedDeepInnerSubCategory(
          ""
        );

        return;
      }

      axios
        .get(
          `https://nissi-india.com/api/deepinnersubcategory_rpt/${innerSubCategoryId}`,
          {
            headers:
              commonHeaders,
          }
        )
        .then((res) =>
          setDeepInnerSubCategoryList(
            res.data
              .deepInnerSubCategoryList ||
              []
          )
        )
        .catch((err) =>
          console.error(
            "Deep Inner Sub Category fetch failed",
            err
          )
        );
    };

  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (
    dateStr
  ) => {
    if (!dateStr) {
      return "";
    }

    const [
      year,
      month,
      day,
    ] = dateStr.split(
      "-"
    );

    return `${day}/${month}/${year}`;
  };

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    if (
      !fromDate ||
      !toDate
    ) {
      toast.error(
        "Please select From Date and To Date"
      );

      return;
    }

    setLoading(true);

    const params = {
      sessionBranchId:
        branchId,

      ddl_Branch:
        String(
          branchId
        ),

      ddl_category:
        selectedCategory ||
        "",

      ddl_subCategory:
        selectedSubCategory ||
        "",

      ddl_innersubcategory:
        selectedInnerSubCategory ||
        "",

      ddl_deepinnersubcategory:
        selectedDeepInnerSubCategory ||
        "",

      txt_itemname:
        "",

      txt_item:
        "",

      txt_FromDate:
        formatDate(
          fromDate
        ),

      txt_ToDate:
        formatDate(
          toDate
        ),

      ddl_Stockwise:
        status ||
        "All",

      financeyearstartdate:
        localStorage.getItem(
          "financeyearstartdate"
        ) || "",
    };

    axios
      .post(
        "https://nissi-india.com/api/stock_summaryRpt",
        params,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data
            .recordset ||
          res.data
            .recordsets?.[0] ||
          [];

        setData(
          rows
        );

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "Stock summary fetch failed",
          err
        );

        toast.error(
          "Failed to fetch stock summary"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     PRINT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handlePrint = () => {
    const params = {
      sessionBranchId:
        branchId,

      ddl_Branch:
        String(
          branchId
        ),

      ddl_category:
        selectedCategory ||
        "",

      ddl_subCategory:
        selectedSubCategory ||
        "",

      ddl_innersubcategory:
        selectedInnerSubCategory ||
        "",

      ddl_deepinnersubcategory:
        selectedDeepInnerSubCategory ||
        "",

      txt_itemname:
        "",

      txt_item:
        "",

      txt_FromDate:
        formatDate(
          fromDate
        ),

      txt_ToDate:
        formatDate(
          toDate
        ),

      ddl_Stockwise:
        status ||
        "All",

      financeyearstartdate:
        localStorage.getItem(
          "financeyearstartdate"
        ) || "",
    };

    const query =
      new URLSearchParams(
        params
      ).toString();

    window.open(
      `/inventory/IndividualItemSummaryPrint?${query}`,
      "_blank"
    );
  };

  /* =========================================================
     CLEAR FILTERS
  ========================================================= */

  const clearFilters = () => {
    setBranchId(
      storedBranchId ||
        "8"
    );

    setFromDate("");

    setToDate("");

    setStatus("");

    setSelectedCategory(
      ""
    );

    setSelectedSubCategory(
      ""
    );

    setSelectedInnerSubCategory(
      ""
    );

    setSelectedDeepInnerSubCategory(
      ""
    );

    setSubCategoryList(
      []
    );

    setInnerSubCategoryList(
      []
    );

    setDeepInnerSubCategoryList(
      []
    );

    setData([]);
  };

  /* =========================================================
     EXPORT
     ORIGINAL PAGE HAS ONLY VISUAL EXPORT BUTTON
  ========================================================= */

  const handleExport = () => {
    toast.info(
      "Export functionality is not implemented in the existing Individual Stock Summary."
    );
  };

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency = (
    amount
  ) => {
    return new Intl.NumberFormat(
      "en-IN",
      {
        style:
          "currency",

        currency:
          "INR",

        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    ).format(
      amount || 0
    );
  };

  /* =========================================================
     NUMBER
  ========================================================= */

  const formatNumber = (
    value
  ) => {
    return value
      ? parseFloat(
          value
        ).toLocaleString(
          "en-IN",
          {
            minimumFractionDigits:
              2,

            maximumFractionDigits:
              2,
          }
        )
      : "0.00";
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Individual Stock Summary
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Boxes className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Individual Stock Summary
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  Detailed analysis of inventory movements and stock levels
                </p>
              </div>
            </div>

            {/* =============================================
                EXPORT + FILTER
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* EXPORT */}

              <button
                type="button"
                onClick={
                  handleExport
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaDownload
                  size={14}
                />

                Export
              </button>

              {/* FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTER BODY
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              {/* =============================================
                  FILTER GRID
              ============================================= */}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date{" "}
                    <span className="text-red-500">
                      *
                    </span>
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                    required
                  />
                </div>

                {/* STOCK STATUS */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Stock Status
                  </label>

                  <select
                    value={
                      status
                    }
                    onChange={(e) =>
                      setStatus(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      All Stock
                    </option>

                    <option value="Zero">
                      Zero Stock
                    </option>

                    <option value="NonZero">
                      Non Zero Stock
                    </option>
                  </select>
                </div>

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      branchId
                    }
                    onChange={(e) =>
                      setBranchId(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Branch
                    </option>

                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_Branchid
                          }
                          value={
                            b.fld_Branchid
                          }
                        >
                          {
                            b.fld_BranchName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* CATEGORY */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Category
                  </label>

                  <select
                    value={
                      selectedCategory
                    }
                    onChange={(e) => {
                      setSelectedCategory(
                        e.target.value
                      );

                      fetchSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Category
                    </option>

                    {categoryList.map(
                      (c) => (
                        <option
                          key={
                            c.fld_itemcategoryid
                          }
                          value={
                            c.fld_itemcategoryid
                          }
                        >
                          {
                            c.fld_itemcategory_name
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* SUB CATEGORY */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Sub Category
                  </label>

                  <select
                    value={
                      selectedSubCategory
                    }
                    onChange={(e) => {
                      setSelectedSubCategory(
                        e.target.value
                      );

                      fetchInnerSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Sub Category
                    </option>

                    {subCategoryList.map(
                      (sc) => (
                        <option
                          key={
                            sc.fld_itemsubCategoryid
                          }
                          value={
                            sc.fld_itemsubCategoryid
                          }
                        >
                          {
                            sc.fld_itemsubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* INNER SUB CATEGORY */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Inner Sub Category
                  </label>

                  <select
                    value={
                      selectedInnerSubCategory
                    }
                    onChange={(e) => {
                      setSelectedInnerSubCategory(
                        e.target.value
                      );

                      fetchDeepInnerSubCategories(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Inner Sub Category
                    </option>

                    {innerSubCategoryList.map(
                      (isc) => (
                        <option
                          key={
                            isc.fld_innersubCategoryid
                          }
                          value={
                            isc.fld_innersubCategoryid
                          }
                        >
                          {
                            isc.fld_innersubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* DEEP INNER SUB CATEGORY */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Item Deep Inner Sub Category
                  </label>

                  <select
                    value={
                      selectedDeepInnerSubCategory
                    }
                    onChange={(e) =>
                      setSelectedDeepInnerSubCategory(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select Deep Inner Sub Category
                    </option>

                    {deepInnerSubCategoryList.map(
                      (disc) => (
                        <option
                          key={
                            disc.fld_deepinnersubCategoryid
                          }
                          value={
                            disc.fld_deepinnersubCategoryid
                          }
                        >
                          {
                            disc.fld_deepinnersubCategoryName
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* CLEAR */}

                <button
                  type="button"
                  onClick={
                    clearFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaTimes
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* PRINT */}

                <button
                  type="button"
                  onClick={
                    handlePrint
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition ${
                    isDark
                      ? "bg-green-900/30 text-green-300 hover:bg-green-900/50"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  <FaPrint
                    size={14}
                  />

                  Print Report
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Individual Stock Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {data.length} records
                </span>
              </div>

              {data.length > 0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading stock records...
              </p>
            </div>
          ) : data.length > 0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Branch
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Item Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      UOM
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Opening Qty
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Opening Value
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Closing Qty
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Closing Value
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {data.map(
                    (
                      item,
                      index
                    ) => (
                      <tr
                        key={
                          item.fld_itemid ||
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* SERIAL */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* BRANCH */}

                        <td className="px-4 py-3 font-medium">
                          {item.fld_branchname ||
                            "-"}
                        </td>

                        {/* ITEM CODE */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {item.fld_itemcode ||
                              "-"}
                          </span>
                        </td>

                        {/* ITEM NAME */}

                        <td className="px-4 py-3">
                          {item.fld_itemname ||
                            "-"}
                        </td>

                        {/* UOM */}

                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.fld_uomname ||
                            "-"}
                        </td>

                        {/* OPENING QTY */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.opqty
                          )}
                        </td>

                        {/* OPENING VALUE */}

                        <td className="px-4 py-3 whitespace-nowrap text-right font-medium">
                          {formatCurrency(
                            item.opamount_New
                          )}
                        </td>

                        {/* CLOSING QTY */}

                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {formatNumber(
                            item.closingQty
                          )}
                        </td>

                        {/* CLOSING VALUE */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatCurrency(
                            item.closingvalue_New
                          )}
                        </td>

                        {/* VIEW */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              window.open(
                                `/inventory/IndividualInterStockReport?branchid=${branchId}&itemid=${item.fld_itemid}&fromdate=${fromDate}&todate=${toDate}`,
                                "_blank"
                              )
                            }
                            className={`inline-flex items-center justify-center p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View Detailed Report"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBox
                className={`mx-auto h-12 w-12 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`mt-3 text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No stock records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Get started by searching with the filters above.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default IndividualStockSummary;